/** Shared commercial form — used by both the Add and Edit pages so the
 * two surfaces are identical (previously edit was a cramped inline row).
 *
 * Individual vs company identity + contact + commission model (percentage
 * / fixed) with per-axis terms + duration. The Edit page additionally
 * shows a status (active / suspended) control via `showStatus`. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CompanyIdentityFields } from "@/components/CompanyIdentityFields";
import PhoneInput from "@/components/PhoneInput";
import { type PartyDetails } from "@/lib/party";

export type CommercialFormValues = {
  name: string;
  email: string;
  phone: string;
  is_company: boolean;
  legal_name: string;
  registration_number: string;
  vat_id: string;
  address_line: string;
  city: string;
  postal_code: string;
  address_country: string;
  commission_model: "percentage" | "fixed";
  rate_base_pct: string;
  rate_session_pct: string;
  rate_signature_pct: string;
  fixed_base_eur: string;
  fixed_session_eur: string;
  fixed_signature_eur: string;
  commission_duration: string;
  status?: string;
};

export function CommercialForm({
  mode,
  initial,
  showStatus = false,
  submitting,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  mode: "create" | "edit";
  initial?: Partial<CommercialFormValues>;
  showStatus?: boolean;
  submitting: boolean;
  error?: boolean;
  submitLabel: string;
  onSubmit: (values: CommercialFormValues) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [isCompany, setIsCompany] = useState(!!initial?.is_company);
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [party, setParty] = useState<PartyDetails>({
    legal_name: initial?.legal_name ?? "",
    registration_number: initial?.registration_number ?? "",
    vat_id: initial?.vat_id ?? "",
    address_line: initial?.address_line ?? "",
    city: initial?.city ?? "",
    postal_code: initial?.postal_code ?? "",
    country: initial?.address_country ?? "",
  });
  const [f, setF] = useState({
    commission_model: (initial?.commission_model ?? "percentage") as
      | "percentage"
      | "fixed",
    rate_base_pct: initial?.rate_base_pct ?? "",
    rate_session_pct: initial?.rate_session_pct ?? "",
    rate_signature_pct: initial?.rate_signature_pct ?? "",
    fixed_base_eur: initial?.fixed_base_eur ?? "",
    fixed_session_eur: initial?.fixed_session_eur ?? "",
    fixed_signature_eur: initial?.fixed_signature_eur ?? "",
    commission_duration: initial?.commission_duration ?? "lifetime",
    status: initial?.status ?? "active",
  });

  const isFixed = f.commission_model === "fixed";
  const axes = isFixed
    ? ([
        ["fixed_base_eur", "platform.partners.baseE"],
        ["fixed_session_eur", "platform.partners.sessionE"],
        ["fixed_signature_eur", "platform.partners.sigE"],
      ] as const)
    : ([
        ["rate_base_pct", "platform.partners.baseP"],
        ["rate_session_pct", "platform.partners.sessionP"],
        ["rate_signature_pct", "platform.partners.sigP"],
      ] as const);
  const canSubmit =
    !!name.trim() && !!email.trim() && (!isCompany || !!party.legal_name.trim());

  function submit() {
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      is_company: isCompany,
      legal_name: isCompany ? party.legal_name : "",
      registration_number: isCompany ? party.registration_number : "",
      vat_id: isCompany ? party.vat_id : "",
      address_line: isCompany ? party.address_line : "",
      city: isCompany ? party.city : "",
      postal_code: isCompany ? party.postal_code : "",
      address_country: isCompany ? party.country : "",
      commission_model: f.commission_model,
      rate_base_pct: f.rate_base_pct,
      rate_session_pct: f.rate_session_pct,
      rate_signature_pct: f.rate_signature_pct,
      fixed_base_eur: f.fixed_base_eur,
      fixed_session_eur: f.fixed_session_eur,
      fixed_signature_eur: f.fixed_signature_eur,
      commission_duration: f.commission_duration,
      ...(showStatus ? { status: f.status } : {}),
    });
  }

  return (
    <section className="card space-y-4">
      {/* Individual vs company */}
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={!isCompany}
            onChange={() => setIsCompany(false)}
          />
          {t("partner.commercials.typeIndividual")}
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={isCompany}
            onChange={() => setIsCompany(true)}
          />
          {t("partner.commercials.typeCompany")}
        </label>
      </div>

      {/* Contact identity */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="label">
            {isCompany
              ? t("partner.commercials.contactName")
              : t("partner.org.colName")}
          </label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">{t("auth.signup.email")}</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label">{t("partner.commercials.phone")}</label>
          <PhoneInput value={phone} onChange={setPhone} />
        </div>
      </div>

      {/* Company identity + address (company only) */}
      {isCompany && (
        <CompanyIdentityFields
          value={party}
          onChange={(patch) => setParty((p) => ({ ...p, ...patch }))}
          idPrefix="commercial"
        />
      )}

      {/* Commission model + per-axis terms */}
      <div className="flex flex-wrap items-end gap-3 border-t border-nb-border/60 pt-3">
        <label className="text-[11px] text-nb-text-3">
          {t("platform.partners.model")}
          <select
            className="input mt-0.5 text-xs"
            value={f.commission_model}
            onChange={(e) =>
              setF((s) => ({
                ...s,
                commission_model: e.target.value as "percentage" | "fixed",
              }))
            }
          >
            <option value="percentage">{t("platform.partners.modelPct")}</option>
            <option value="fixed">{t("platform.partners.modelFixed")}</option>
          </select>
        </label>
        {axes.map(([key, label]) => (
          <label key={key} className="text-[11px] text-nb-text-3">
            {t(label)}
            <input
              className="input mt-0.5 w-20 text-xs"
              type="number"
              step="0.01"
              value={f[key]}
              onChange={(e) => setF((s) => ({ ...s, [key]: e.target.value }))}
            />
          </label>
        ))}
        <label className="text-[11px] text-nb-text-3">
          {t("platform.partners.duration")}
          <select
            className="input mt-0.5 text-xs"
            value={f.commission_duration}
            onChange={(e) =>
              setF((s) => ({ ...s, commission_duration: e.target.value }))
            }
          >
            <option value="lifetime">{t("platform.partners.durationOption.lifetime")}</option>
            <option value="12_months">{t("platform.partners.durationOption.12_months")}</option>
            <option value="one_time">{t("platform.partners.durationOption.one_time")}</option>
          </select>
        </label>
        {showStatus && (
          <label className="text-[11px] text-nb-text-3">
            {t("platform.common.status")}
            <select
              className="input mt-0.5 text-xs"
              value={f.status}
              onChange={(e) => setF((s) => ({ ...s, status: e.target.value }))}
            >
              <option value="active">{t("platform.partners.statusOption.active")}</option>
              <option value="suspended">{t("platform.partners.statusOption.suspended")}</option>
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-nb-border/60 pt-3">
        <button
          type="button"
          className="btn-primary text-sm"
          disabled={submitting || !canSubmit}
          onClick={submit}
        >
          {submitting ? t("common.saving", "Saving…") : submitLabel}
        </button>
        <button type="button" className="btn-ghost text-sm" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        {error && (
          <span className="text-xs text-cvp-danger">
            {mode === "create"
              ? t("partner.commercials.createError")
              : t("partner.commercials.updateError", "Could not save changes.")}
          </span>
        )}
      </div>
    </section>
  );
}
